/**
 * IngredientList Component
 *
 * Displays recipe ingredients with checkboxes for tracking,
 * quantity/unit info, optional badges, and substitutes.
 */

import { useState } from 'react';
import type { RecipeIngredient } from '@/types/food';

interface IngredientListProps {
  ingredients: RecipeIngredient[];
}

export function IngredientList({ ingredients }: IngredientListProps) {
  const [checked, setChecked] = useState<Set<number>>(new Set());

  const toggleIngredient = (id: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatQuantity = (ingredient: RecipeIngredient): string => {
    if (!ingredient.quantity && !ingredient.unit) {
      return '';
    }
    const qty = ingredient.quantity ?? '';
    const unit = ingredient.unit ?? '';
    return ` - ${qty}${unit}`;
  };

  return (
    <ul className="list-none">
      {ingredients.map((ingredient) => (
        <li
          key={ingredient.id}
          className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-b-0"
        >
          <input
            type="checkbox"
            checked={checked.has(ingredient.id)}
            onChange={() => toggleIngredient(ingredient.id)}
            className="w-5 h-5 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-label={`Mark ${ingredient.name} as checked`}
          />
          <div className="flex-1">
            <div>
              <span
                className={`font-medium ${checked.has(ingredient.id) ? 'line-through text-gray-400' : 'text-gray-900'}`}
              >
                {ingredient.name}
              </span>
              <span className="text-gray-500 text-sm">
                {formatQuantity(ingredient)}
              </span>
              {ingredient.optional && (
                <span className="inline-block ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded uppercase">
                  Optional
                </span>
              )}
            </div>
            {ingredient.notes && (
              <div className="text-sm text-gray-400 italic mt-0.5">
                {ingredient.notes}
              </div>
            )}
            {ingredient.substitutes.length > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                <span className="font-semibold text-gray-600">Substitutes:</span>{' '}
                {ingredient.substitutes.map((sub) => sub.name).join(', ')}
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
